import ConfigurationOptions from '../layouts/ConfigurationOptions.jsx';
import ConfigurationTableSearch, { parameterTableRows } from './ConfigurationTableSearch';
import ClassifierFields, { classifierSettings, classifierError, classifierOutputOptions } from './ClassifierFields';
import React, { useState, useRef, useEffect } from 'react';
import server from '../../api/server.js';
import { FaRegEdit, FaTimes } from "react-icons/fa";
import "../loader.css"
import { Archive, Info, Save, Upload, Download } from 'lucide-react';
import ModernTimeDropdown from '../layouts/modernDropdown.jsx';
import { useToast } from '../../context/ToastContext.jsx';
import SampleTestModal from '../layouts/SampleTestModal.jsx'
import { IoChatbubbleOutline } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import { TbRestore } from "react-icons/tb";
import FeedbackSection from '../layouts/FeedbackSection.jsx';
import DeployConfirmationModal from '../layouts/DeployConfirmationModal.jsx';
import { getApiToastMessage } from '../../utils/apiToastMessage.js';
import { findBoundarySpaceName, hasBoundarySpace, getBoundarySpaceNameError } from '../../utils/moduleNameValidation.js';
import ConfigurationDiffButton from '../layouts/ConfigurationDiffButton.jsx';

const callTypeOptions = [
  ['run_for_non_meaningful_calls', 'Non-Meaningful calls'],
  ['run_for_not_connected_calls', 'Not Connected calls'],
];
const callTypeSettings = (value = {}) => Object.fromEntries(
  callTypeOptions.filter(([key]) => value[key] !== undefined).map(([key]) => [key, value[key]])
);

const moduleCallTypeSettings = (value = {}) => Object.fromEntries(
  callTypeOptions.map(([key]) => [key, value[key] ?? 0])
);

const configWithExecutionSettings = (configuration, settings) => ({
  configuration,
  ...moduleCallTypeSettings(settings),
});

const BusinessInsightConfigure = ({clientID, serviceID, module_name, setCloseModal, config_data}) => {
  const [moduleExecution, setModuleExecution] = useState(moduleCallTypeSettings());
  const [loadedModuleExecution, setLoadedModuleExecution] = useState(moduleCallTypeSettings());
  const [tableSearch, setTableSearch] = useState({});
  const { showToast } = useToast();
  const parameterRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeployConfirmation, setShowDeployConfirmation] = useState(false);
  const [current_version, setCurrent_version] = useState(0)
  const [businessInsightsModule, setBusinessInsightModule] = useState([])
  const [moduleSaveDraftConfiguration, setModuleSaveDraftConfiguration] = useState(null)
  const [configType, setConfigType] = useState('Live Version')
  const [showConfigDropdown, setShowConfigDropdown] = useState(false)
  const [showMoreMap, setShowMoreMap] = useState({});
  const [editingParameter, setEditingParameter] = useState(null);
  const [newOption, setNewOption] = useState('');
  const [moduleActive, setModuleActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [newTag, setNewTag] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [defaultActive, setDefaultActive] = useState(false)
  const [showArchivedParameters, setShowArchivedParameters] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveParameter, setArchiveParameter] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(null);
  const nalist = ['Not Determined','Not Applicable','Not Available','Unknown','NA','Not Relevant','Not Provided','Undefined','Indeterminate','Data Missing','Unavailable','Missing','Undetermined','None', 'Not Found', 'Not Discussed']
  const [showSampleTest, setShowSampleTest] = useState(false)
  const [showSampleTestModal, setShowSampleTestModal] = useState(false)
  const sourceClickInProgressRef = useRef(false);
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  const [descriptionError, setDescriptionError] = useState(null)
  const [showLoadConfigurePart, setShowLoadConfigurePart] = useState(false)
  
  // Upload and Download Configuration Functions
  const handleDownloadConfig = () => {
    try {
      const configData = {
        businessInsightsModule: businessInsightsModule.map(param => ({
          id: param.id,
          name: param.name,
          definition: param.definition,
          context_scope: param.context_scope || 'conversation',
          ...callTypeSettings(param),
          ...classifierSettings(param),
          type: param.type,
          options: param.options || [],
          active: param.active,
          status: param.status || 'active',
          tag: param.tag || ''
        })),
        ...moduleExecution, moduleActive: moduleActive,
        availableTags: availableTags || []
      };

      const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `business_insights_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Configuration downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading configuration:', error);
      showToast('Error downloading configuration', 'error');
    }
  };

  const handleUploadConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const configData = JSON.parse(e.target.result);
        setLoading(true);
        const boundarySpaceError = findBoundarySpaceName(configData.businessInsightsModule || [], 'name', 'Parameter name');
        if (boundarySpaceError) {
          showToast(boundarySpaceError, 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        const uploadedBusinessInsights = {};
        (configData.businessInsightsModule || []).forEach(item => {
          const key = item?.name?.toLowerCase().replace(/\s+/g, "_");
          if (!key) return;
          let backendType = item.type;
          if (item.type === 'Categorical - Single Value') {
            backendType = 'Categorical';
          }
          uploadedBusinessInsights[key] = {
            instruction: item.definition,
            context_scope: item.context_scope || 'conversation',
            ...callTypeSettings(item),
            ...classifierSettings(item),
            output_type: backendType,
            display_name: item.name,
            possible_values: item.type === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : item.options,
            status: item.status || 'active',
            active: item.status === 'active',
            tag: item.tag
          };
        });
        const validationConfig = {
          configuration: {...uploadedBusinessInsights},
          ...moduleCallTypeSettings(configData), active: configData.moduleActive === true ? 1 : 0
        };
        const validationResp = await server.validateModuleJsonConfiguration({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'business_insights', configuration: validationConfig});
        if (validationResp.status !== 200) {
          showToast(await getApiToastMessage(validationResp, 'Invalid configuration'), 'error')
          setLoading(false)
          event.target.value = '';
          return;
        }
        
        // Validate the uploaded configuration
        if (!configData.businessInsightsModule) {
          throw new Error('Invalid configuration format');
        }

        // Update the state with uploaded configuration
        if (configData.businessInsightsModule) {
          setBusinessInsightModule(configData.businessInsightsModule);
        }
        
        if (configData.moduleActive !== undefined) {
          setModuleActive(configData.moduleActive);
        }
        setModuleExecution(moduleCallTypeSettings(configData));
        
        if (configData.availableTags) {
          setAvailableTags(configData.availableTags);
          setTags(configData.availableTags);
        }

        showToast(await getApiToastMessage(validationResp, 'Configuration uploaded successfully!'), 'success');
        setLoading(false)
      } catch (error) {
        console.error('Error parsing uploaded configuration:', error);
        showToast('Error uploading configuration: Invalid JSON format', 'error');
        setLoading(false)
      }
    };
    
    reader.readAsText(file);
  };

  const convertToSeconds = (time) => {
    if(typeof time === 'string'){
      const [hours, minutes, seconds] = time.split(':').map(Number);
      return (hours * 3600) + (minutes * 60) + seconds;
    }
    return time;
  }
  const handleSourceClick = (source, segments) => {
    let start, end, message_id;
    if(source.start_timestamp && source.end_timestamp){
      // Set flag to prevent automatic segment selection
      sourceClickInProgressRef.current = true;
      
      // Convert timestamps to seconds with proper validation
      try {
        if(typeof source.start_timestamp === 'string'){
          start = convertToSeconds(source.start_timestamp)-1;
        }else{
          start = parseFloat(source.start_timestamp)-1;
        }
        if(typeof source.end_timestamp === 'string'){
          end = convertToSeconds(source.end_timestamp)+1;
        }else{
          end = parseFloat(source.end_timestamp)+1;
        }
        
        // Validate that start and end are valid numbers
        if (isNaN(start) || isNaN(end)) {
          console.warn('Invalid timestamp values:', source.start_timestamp, source.end_timestamp);
          sourceClickInProgressRef.current = false;
          return;
        }
        
        // Ensure start time is always less than end time
        if (start >= end) {
          console.warn('Start time must be less than end time:', start, end);
          sourceClickInProgressRef.current = false;
          return;
        }
        
      } catch (error) {
        console.warn('Error converting timestamps:', error);
        sourceClickInProgressRef.current = false;
        return;
      }
      
      // Handle audio case
      // Check if audio element exists and is valid
      if (!audioRef.current) {
        console.warn('Audio element not available');
        sourceClickInProgressRef.current = false;
        return;
      }

      try {
        // Pause any currently playing audio first
        audioRef.current.pause();
        
        // Remove any existing timeupdate listeners
        if (audioRef.current._currentTimeUpdateHandler) {
          audioRef.current.removeEventListener('timeupdate', audioRef.current._currentTimeUpdateHandler);
          audioRef.current._currentTimeUpdateHandler = null;
        }
        
        // Create new timeupdate handler
        const timeUpdateHandler = () => {
          if (!audioRef.current) return;
          
          const currentTime = audioRef.current.currentTime;
          
          if (currentTime >= end) {
            audioRef.current.pause();
            audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
            audioRef.current._currentTimeUpdateHandler = null;
          }
        };
        
        // Store reference to handler for cleanup
        audioRef.current._currentTimeUpdateHandler = timeUpdateHandler;
        
        // Add the new listener
        audioRef.current.addEventListener('timeupdate', timeUpdateHandler);
        
        // Seek to start time
        audioRef.current.currentTime = start;
        
        // Play with proper error handling
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Find the segment index whose range includes the start time
              let idx = 0;
              for (let i = 0; i < segments.length; i++) {
                if (start >= segments[i].start_time && start <= segments[i].end_time) {
                  idx = i;
                  break;
                }
              }
              setSelectedSegmentIdx(idx);
              
              // Scroll the segment into view
              if (segmentRefs.current[idx] && idx === selectedSegmentIdx) {
                segmentRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "nearest" });
              }
              
              // Clear the flag after successful playback start
              sourceClickInProgressRef.current = false;
            })
            .catch(error => {
              console.warn('Error playing audio:', error);
              // Clean up listener on error
              audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
              audioRef.current._currentTimeUpdateHandler = null;
              // Clear the flag on error
              sourceClickInProgressRef.current = false;
            });
        }
      } catch (error) {
        console.warn('Error handling source click:', error);
        sourceClickInProgressRef.current = false;
      }
    } else {
      // Handle chat message case
      message_id = source.segment_id;
      if(message_id){
        const message = segments.find(message => message.message_id === message_id);
        if(message){
          // Find the index of the message in chatTranscriptions
          const messageIndex = segments.findIndex(msg => msg.message_id === message_id);
          if (messageIndex !== -1) {
            // Create a ref for the message element if it doesn't exist
            if (!segmentRefs.current[messageIndex]) {
              segmentRefs.current[messageIndex] = React.createRef();
            }
            
            // Scroll the message into view
            setTimeout(() => {
              const messageElement = document.querySelector(`[data-message-id="${message_id}"]`);
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
                // Add a temporary highlight effect
                messageElement.style.backgroundColor = "rgba(111, 111, 254, 0.1)";
                setTimeout(() => {
                  messageElement.style.backgroundColor = "";
                }, 2000);
              }
            }, 100);
          }
        }
      }
    }
  }

  const audioRef = useRef()
  const segmentRefs = useRef([]);
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(null);
  const scrollToSegment = (idx) => {
    const el = segmentRefs.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };  
  const toggleSegment = (item, idx, link) => {
    setSelectedSegmentIdx(idx);
    scrollToSegment(idx);
    if(!link){
      return;
    }
    if (audioRef.current) {
      audioRef.current.currentTime = item.start_time; // Seek to the start time of the segment
      audioRef.current.play(); // Play the audio
    }
  };
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  function formatEpochTime(epochMillis) {
    if (typeof epochMillis !== 'number' || epochMillis.toString().length !== 13) {
      return ""
    }
  
    const date = new Date(epochMillis);
    return date.toLocaleString(); // e.g., "5/29/2025, 2:30:45 PM"
  }
  const handleAudioTimeUpdate = (segments) => {
    if (sourceClickInProgressRef.current) return;
    const currentTime = audioRef.current.currentTime;

    // Find the segment corresponding to the current audio time
    segments.forEach((segment, idx) => {
      if (currentTime >= segment.start_time && currentTime <= segment.end_time) {
        if (segmentRefs.current[idx]) {
          // Scroll the corresponding segment into view if it's not already visible
          segmentRefs.current[idx].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });

          // Highlight the selected segment
          setSelectedSegmentIdx(idx);
        }
      }
    });
  };
  const getBorderColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"4px solid #6f6ffe"
      case 'agent':
        return"4px solid #3B82F6"
      case 'bot':
        return"4px solid gray"
      case 'system':
        return "4px solid gray"
      default:
        break;
    }
  }
  const getBackgroundColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"rgb(249, 250, 251)"
      case 'agent':
        return"rgb(249, 250, 251)"
      case 'bot':
        return"rgb(249, 250, 251)"
      case 'system':
        return"rgb(249, 250, 251)"
      default:
        break;
    }
  }

  const handleSelectTag = (tag) => {
    setSelectedTag(tag);
    setEditingParameter({...editingParameter, tag})
    setIsOpen(false);
  };
  const handleCloseSampleTestModal = () => {
    setShowSampleTestModal(false)
  }

  const handleAddNewTag = () => {
    if (newTag.trim() !== '') {
      setTags([...tags, newTag]);
      setSelectedTag(newTag);
      setEditingParameter({...editingParameter, 'tag': newTag})
      setNewTag('');
      setIsAddingNew(false);
      setIsOpen(false);
    }
  };

  const [previewData, setPreviewData] = useState([]);
  const handleAddParameter = () => {
    const newParameter = {
      id: 0, // Temporary ID for new parameters
      name: "",
      definition: "",
      type: "Categorical - Single Value",
      active: true,
      status: "active", // Default status for new parameters
      tag: "",
      options: ['NA']
    };
    setEditingParameter(newParameter);
    setTimeout(() => {
      if (parameterRef.current) {
        parameterRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const [availableTags, setAvailableTags] = useState([]);

  const [suggestionsVisible, setSuggestionsVisible] = useState(false);

  const filteredTags = availableTags.filter(tag =>
    tag && tag?.toLowerCase().includes(editingParameter?.tag?.toLowerCase()) &&
    editingParameter?.tag !== ""
  );

  const handleEditParameter = (parameter) => {
    setEditingParameter({...parameter});
    setSelectedTag(parameter.tag || ''); // Set the selectedTag when editing a parameter
    setTimeout(() => {
      if (parameterRef.current) {
        parameterRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };
  const normalizeName = (name) => {
    if (typeof name !== "string") return "";
    return name.trim().replace(/\s+/g, " ");
  };
  const handleDeleteParameter = (parameter) => {
    setBusinessInsightModule(businessInsightsModule.map(p => 
      p.id === parameter.id ? {...p, status: 'archived'} : p
    ));
  };
  const getFilteredParameters = () => {
    if (showArchivedParameters) {
      // Show only archived parameters
      return businessInsightsModule.filter(param => param.status === 'archived');
    } else {
      // Show only active and inactive parameters, hide archived ones
      return businessInsightsModule.filter(param => 
        !param.status || param.status === 'active' || param.status === 'inactive'
      );
    }
  };
  const handleArchiveParameter = (parameter) => {
    setBusinessInsightModule(businessInsightsModule.map(p => 
      p.id === parameter.id ? {...p, status: 'archived'} : p
    ));
  };

  const ensureParameterStatus = (parameter) => {
    return {
      ...parameter,
      status: parameter.status || 'active'
    };
  };

  const handleRestoreParameter = (parameter) => {
    setBusinessInsightModule(businessInsightsModule.map(p => 
      p.id === parameter.id ? {...p, status: 'active'} : p
    ));
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ text, children, id, position = "top" }) => {
    return (
      <div 
        className="relative inline-block"
        onMouseEnter={() => setTooltipVisible(id)}
        onMouseLeave={() => setTooltipVisible(null)}
      >
        {children}
        {tooltipVisible === id && (
          <div className={`absolute px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap z-50 ${
            position === "left" 
              ? "right-full top-1/2 transform -translate-y-1/2 mr-2" 
              : "bottom-full left-1/2 transform -translate-x-1/2 mb-2"
          }`}>
            {text}
            {position === "left" ? (
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-2 border-transparent border-l-gray-800"></div>
            ) : (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-2 border-transparent border-t-gray-800"></div>
            )}
          </div>
        )}
      </div>
    );
  };

  const checkDefinitionValidity = async (definition, type, options) => {
    if(type === 'Categorical - Single Value' || type === 'Categorical - Multiple Values' || type === 'Yes/No/NA' || type === 'Number' || type === 'Date and Time'){
      let res = await server.checkDefinitionValidity(client_id, service_id, email, 'apps', undefined, definition, type, options, 'business_insights')
      let resp = await res.json()
      if(res.status === 200){
        if(resp.data){
          if(resp.data.error){
            return {error: resp.data.error}
          }else{
            return {error: null}
          }
        }
      }else{
        return {error: resp.error}
      }
    }
  }

  const handleSaveParameter = async () => {
    setShowLoadConfigurePart(true)
    if (hasBoundarySpace(editingParameter?.name)) {
      const errorMessage = getBoundarySpaceNameError('Parameter name', editingParameter?.name);
      setError(errorMessage);
      showToast(errorMessage, "error");
      setShowLoadConfigurePart(false)
      return;
    }
    const normalizedName = normalizeName(editingParameter.name);
  
    if (!normalizedName) {
      alert("Parameter name cannot be empty");
      setShowLoadConfigurePart(false)
      return;
    }
  
    let flag = false;
    for (let i = 0; i < businessInsightsModule.length; i++) {
      const current = businessInsightsModule[i];
      if (!current || typeof current.name !== 'string') {
        continue; // skip invalid entries that aren't parameters
      }
      const existingName = normalizeName(current.name);
      if (
        existingName.toLowerCase() === normalizedName.toLowerCase() &&
        editingParameter.id === 0
      ) {
        flag = true;
        break;
      }
    }
  
    if (flag) {
      setError("Parameter name already exists.");
      setShowLoadConfigurePart(false)
      return;
    } else {
      setError("");
    }
  
    let updatedParam = {
      ...editingParameter,
      name: normalizedName,
    };

    if (updatedParam.subparameter_type !== 'classifier' && (updatedParam.type === "Categorical - Single Value" || updatedParam.type === "Categorical - Multiple Values")) {
      const lowercasedOptions = updatedParam.options.map(opt => opt.toLowerCase());
      const lowercasedNaList = nalist.map(item => item.toLowerCase());
  
      const hasNaOption = lowercasedOptions.some(opt => lowercasedNaList.includes(opt));
  
      if (!hasNaOption) {
        updatedParam = {
          ...updatedParam,
          options: [...updatedParam.options, "NA"]
        };
      }
    }
  
    if (
      (updatedParam.type === "Categorical - Single Value" || updatedParam.type === "Categorical - Multiple Values") &&
      updatedParam.options.length === 0
    ) {
      alert("Categorical parameter must have at least one option");
      setShowLoadConfigurePart(false)
      return;
    }
    const classificationError = classifierError(updatedParam);
    if (classificationError) {
      showToast(classificationError, 'error');
      setShowLoadConfigurePart(false);
      return;
    }
    if(updatedParam.subparameter_type !== 'classifier' && (updatedParam.type === 'Categorical - Single Value' || updatedParam.type === 'Categorical - Multiple Values' || updatedParam.type === 'Yes/No/NA' || updatedParam.type === 'Number' || updatedParam.type === 'Date and Time')){
      let flagResponse = await checkDefinitionValidity(updatedParam.definition, updatedParam.type, updatedParam.options)
      if(flagResponse['error']){
        setDescriptionError(flagResponse['error'])
        showToast(flagResponse['error'], "error")
        setShowLoadConfigurePart(false)
        return;
      }else{
        setDescriptionError(null)
        showToast("Parameter saved successfully", "success")
      }
    }
  
    if (updatedParam.id === 0) {
      const newId =
        businessInsightsModule.length > 0
          ? Math.max(...businessInsightsModule.map((p) => p.id)) + 1
          : 1;
  
      setBusinessInsightModule([
        ...businessInsightsModule,
        { ...updatedParam, id: newId },
      ]);
    } else {
      const updatedParameters = businessInsightsModule.map((param) =>
        param.id === updatedParam.id ? updatedParam : param
      );
      setBusinessInsightModule(updatedParameters);
    }
  
    setEditingParameter(null);
    setShowLoadConfigurePart(false)
  };
  
  

  const handleCancelEdit = () => {
    setEditingParameter(null);
  };

  const handleChangeParameterType = (type) => {
    const DATE_TIME_LABEL = 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)';
    const normalized = type === DATE_TIME_LABEL ? 'Date and Time' : type;
    setEditingParameter({
      ...editingParameter,
      type: normalized,
      options: (normalized === "Categorical - Single Value" || normalized === "Categorical - Multiple Values")
        ? editingParameter.options
        : (normalized === 'Yes/No/NA') ? ['Yes','No','NA'] : []
    });
  };
  const handleEditStatus = (status) => {
    setEditingParameter({editingParameter, active: !status})
  }

  const handleAddOption = () => {
    if (!newOption.trim()) return;
    
    setEditingParameter({
      ...editingParameter,
      options: [...editingParameter.options, newOption.trim()]
    });
    
    setNewOption('');
  };

  const handleDeleteOption = (option) => {
    setEditingParameter({
      ...editingParameter,
      options: editingParameter.options.filter(o => o !== option)
    });
  };
  const [expandedSamples, setExpandedSamples] = useState(previewData.map(item => false));

  const toggleSample = (index) => {
    setExpandedSamples((prev) => {
      const newExpandedSamples = [...prev];
      newExpandedSamples[index] = !newExpandedSamples[index];
      return newExpandedSamples;
    });
  };
  const handleToggleSubparameter = (id) => {
    setBusinessInsightModule(businessInsightsModule.map(param => 
      param.id === id ? {...param, active: !param.active} : param
    ));
  };

  const [email, setEmail] = useState(null)
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const [moduleName, setModuleName] = useState(null)
  const [moduleConfig, setModuleConfig] = useState(null)
  const [defaultConfig, setDefaultConfig] = useState(null)
  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }
  const getDefaultModuleConfigs = async () => {
    try {
      setLoading(true);
      let res = await server.getDefaultModuleConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
      if(res.status === 200){
        let resp = await res.json()
        if (resp.data){
          setDefaultConfig(resp.data)
        }
      }
    setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const getModuleConfig = async () => {
    try {
        let res = await server.getClientModuleConfig(client_id, service_id, email, 'apps', undefined, moduleName);
        let res1 = await server.getBusinessInsightsTags(client_id, service_id, email, 'apps', undefined)
        if (res.status === 200) {
            let resp = await res.json();
            let resp1 = await res1.json();

            if (resp.data.configuration) {
              setModuleActive(resp.data.active===1 ? true : false);
              setModuleExecution(moduleCallTypeSettings(resp.data));
              setLoadedModuleExecution(moduleCallTypeSettings(resp.data));
              setModuleConfig(resp.data.configuration)
              setModuleSaveDraftConfiguration(resp.data.configuration)
              setCurrent_version(resp.data.version)
                const transformedData = Object.entries(resp.data.configuration)
                  .filter(([key, item]) => item && typeof item === 'object' && (item.output_type || item.display_name || item.instruction))
                  .map(([key, item], index) => {
                    // Convert backend types to frontend display types
                    let displayType = item.output_type;
                    if (item.output_type === 'Categorical') {
                      displayType = 'Categorical - Single Value';
                    }
                    return {
                      id: index + 1,
                      name: item.display_name,
                      definition: item.instruction,
                      context_scope: item.context_scope || 'conversation',
                      ...callTypeSettings(item),
                      ...classifierSettings(item),
                      type: displayType,
                      options: item.possible_values || [],
                      active: item.active,
                      status: item.status || 'active',
                      tag: item.tag
                    };
                  });
                // setDefaultConfig(resp.data.configuration)

                setBusinessInsightModule(transformedData);
            }
            if (resp1.data){
              setAvailableTags(resp1.data)
              setTags(resp1.data)
            }
        }
    } catch (error) {
    }
};
const getModuleDraftSaveConfiguration = async () => {
  try {
    let res = await server.getModuleDraftSaveConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
    if(res.status === 200){
      let resp = await res.json()
      if(resp.data){
        setModuleConfig(resp.data.configuration)
        setModuleSaveDraftConfiguration(resp.data.configuration)
        const transformedData = Object.entries(resp.data.configuration)
          .filter(([key, item]) => item && typeof item === 'object' && (item.output_type || item.display_name || item.instruction))
          .map(([key, item], index) => {
            // Convert backend types to frontend display types
            let displayType = item.output_type;
            if (item.output_type === 'Categorical') {
              displayType = 'Categorical - Single Value';
            }
            return {
                id: index + 1,
                name: item.display_name,
                definition: item.instruction,
                context_scope: item.context_scope || 'conversation',
                ...callTypeSettings(item),
                ...classifierSettings(item),
                type: displayType,
                options: item.possible_values || [],
                active: item.active,
                status: item.status || 'active',
                tag: item.tag
              };
          });
        // setDefaultConfig(resp.data.configuration)

      setBusinessInsightModule(transformedData);
      setModuleActive(resp.data.active === 1 ? true : false)
      setModuleExecution(moduleCallTypeSettings(resp.data));
      setLoadedModuleExecution(moduleCallTypeSettings(resp.data));
      }else{
        showToast('No saved draft.', "error")
        setConfigType('Live Version')
      }
      // resetToDefault('draft')
    }
  } catch (error) {
  }
}

const handleGenerateSampleOutputs = async(sample_flag) => {
  try {
    const businessInsightsFormatted = convertToBusinessInsightsFormat();
    let configData = businessInsightsFormatted['formattedConfig']
    let config = {'configuration': {...configData}, ...moduleExecution, 'active': moduleActive===true ? 1 : 0}
    setLoading(true)
      let resp = await server.generateSampleModuleOutputs({
        client_id: client_id,
        service_id: Array.isArray(service_id) ? service_id[0] : service_id,
        module_name: 'business_insights',
        configuration: config,
        menu: "apps",
        submenu: undefined,
        email: email,
        'sample_set_flag': sample_flag
      })
    if (resp.status === 200) {
      let respData = await resp.json();
      setPreviewData(respData.data)
    }
    setLoading(false)
  } catch (error) {
    setLoading(false)
  }
}
const getProjectStatus = async () => {
  try {
    let res = await server.getProjectStatus(client_id, service_id, email, 'apps', undefined, window.location.href)
    if(res.status === 200){
      let resp = await res.json()
      if(resp.data && resp.data === 'completed'){
        setShowSampleTest(true);
      }
    }
  } catch (error) {
  }
}

const convertToBusinessInsightsFormat = () => {
  const formattedConfig = {};

  businessInsightsModule.forEach(item => {
      // Convert the name to snake_case (lowercase with underscores)
      const key = item?.name?.toLowerCase().replace(/\s+/g, "_");

      // Convert frontend display types to backend expected types
      let backendType = item.type;
      if (item.type === 'Categorical - Single Value') {
        backendType = 'Categorical';
      }
      
      formattedConfig[key] = {
        instruction: item.definition,
        context_scope: item.context_scope || 'conversation',
        ...callTypeSettings(item),
        ...classifierSettings(item),
        output_type: backendType,
        display_name: item.name,
        possible_values: item.type === 'Yes/No/NA' ? ['Yes', 'No', 'NA'] : item.options,
        status: item.status || 'active', // Include status in the configuration
        active: item.status === 'active', // Set active based on status
        tag: item.tag
      };
  });
  
  const arr = [...new Set(
    businessInsightsModule
      .filter(item => item.status !== 'archived') // Only include non-archived parameters for tags
      .map(item => item.tag)
      .filter(Boolean)
  )];  

  return {formattedConfig, 'savedTags': arr};
};
const [error, setError] = useState("")
  const handleChangeParams = (e) => {
    const value = e.target.value;
    const alphanumericWithSpaceRegex = /^[a-zA-Z0-9 ]*$/;
    
    if (!alphanumericWithSpaceRegex.test(value)) {
      setError("Only alphanumeric characters and spaces are allowed");
      return;
    }
    
    if (value.length <= 30) {
      setEditingParameter({...editingParameter, name: value})
      setError(""); // Clear error when within limit
    } else {
      setError("Maximum 30 characters allowed");
    }
};

const deployModule = async(versionDescription) => {
  try {
    setLoading(true)
    let deployed = false;
    let errorMessage = 'Failed to deploy module';
    const businessInsightsFormatted = convertToBusinessInsightsFormat();
    let configData = businessInsightsFormatted['formattedConfig']
    let tags = businessInsightsFormatted['savedTags']
    let config = {'configuration': {...configData}, ...moduleExecution, 'active': moduleActive===true ? 1 : 0}
    let resp = await server.deployModule({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'business_insights', configuration: config, default: defaultActive, 'version_changed': current_version, version_description: versionDescription})
    if (resp.status === 200) {
      await server.postBusinessInsightTag({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, tag: tags})
      showToast('Module deployed successfully', "success")
      setLoading(false)
      deployed = true;
    }else{
      try {
        const errData = await resp.clone().json();
        errorMessage = errData?.message || errData?.error || errorMessage;
      } catch (_) {}
    }
    if (deployed) {
      setCloseModal()
    } else {
      showToast(errorMessage, 'error');
      setLoading(false)
    }
  } catch (error) {
    showToast('Failed to deploy module', 'error');
    setLoading(false)
  }
}

const toggleShowMore = (key) => {
  setShowMoreMap((prev) => ({ ...prev, [key]: !prev[key] }));
};

const resetToDefault = () => {
  const transformedData = Object.entries(defaultConfig.configuration)
    .filter(([key, item]) => item && typeof item === 'object' && (item.output_type || item.display_name || item.instruction))
    .map(([key, item], index) => {
      // Convert backend types to frontend display types
      let displayType = item.output_type;
      if (item.output_type === 'Categorical') {
        displayType = 'Categorical - Single Value';
      }
      
      return {
          id: index + 1,
          name: item.display_name,
          definition: item.instruction,
          context_scope: item.context_scope || 'conversation',
          ...callTypeSettings(item),
          ...classifierSettings(item),
          type: displayType,
          options: item.possible_values || [],
          active: item.active,
          status: item.status || 'active'
        };
    });
  setDefaultActive(true)
  setBusinessInsightModule(transformedData)
  setModuleActive(true);
  setModuleExecution(moduleCallTypeSettings(defaultConfig || {}));
  setLoadedModuleExecution(moduleCallTypeSettings(defaultConfig || {}));
}
  // Load new audio when sample changes
  useEffect(() => {
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
        if (user && clientID) {
          setEmail(user.email);
          setClient_id(clientID);
        }
        if (serviceID && module_name) {
          setService_id(serviceID);
          setModuleName(module_name)
        }
      }, [clientID, serviceID, module_name]);
    
      useEffect(() => {
        if (client_id && service_id && moduleName && email) {
          getModuleConfig();
          getDefaultModuleConfigs()
          getProjectStatus()
        }
      }, [client_id, service_id, moduleName, email]);
  
      useEffect(()=> {
        if(configType === 'Saved Draft'){
          getModuleDraftSaveConfiguration();
        }
        if(configType === 'Default'){
          resetToDefault();
        }
        if(configType === 'Live Version' && client_id && service_id && moduleName && email){
          getModuleConfig();
        }
      }, [configType])
      const handleUpdateConfig = async () => {
        try {
          const businessInsightsFormatted = convertToBusinessInsightsFormat();
          let configData = businessInsightsFormatted['formattedConfig']
      let config = {'configuration': {...configData}, ...moduleExecution, 'active': moduleActive===true ? 1 : 0}
          let res = await server.postModuleDraftSaveConfiguration({client_id, service_id, email, module_name: 'business_insights', 'configuration': config, 'menu': 'apps', 'submenu': undefined})
          if(res.status === 200) {
            showToast(await getApiToastMessage(res, 'Draft saved successfully'), "success")
          } else {
            showToast(await getApiToastMessage(res, 'Failed to save draft'), "error")
          }
        } catch (error) {
          showToast('Failed to save draft', "error")
      }
    }
      useEffect(()=> {
        if(moduleSaveDraftConfiguration){
          const businessInsightsFormatted = convertToBusinessInsightsFormat();
          let configData = businessInsightsFormatted['formattedConfig']
        let config = {'configuration': {...configData}, ...moduleExecution, 'active': moduleActive===true ? 1 : 0}
          config_data(config)
        }
      }, [moduleSaveDraftConfiguration, moduleActive, moduleExecution, businessInsightsModule])
  
  const getCurrentConfigForDiff = () => {
    const businessInsightsFormatted = convertToBusinessInsightsFormat();
    return businessInsightsFormatted.formattedConfig;
  };

  const getLoadedConfigForDiff = () => {
    if (configType === 'Default') return defaultConfig?.configuration;
    if (configType === 'Saved Draft') return moduleSaveDraftConfiguration;
    return moduleConfig || moduleSaveDraftConfiguration;
  };

  // Configuration Screen
  if (!showPreview) {
    return (
      <div className="p-3 h-full min-h-0 overflow-hidden">
        <div className="mx-auto flex h-full min-h-0 flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 shrink-0">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">Business Insights 
                <span className='inline-flex items-center px-2 py-1 rounded-full whitespace-nowrap font-normal text-xs bg-gray-200 ml-1' style={{color: "gray"}}>{configType.replace(/^Reset to /, '')}</span>
                </h1>
              <p className="text-gray-500 text-sm mt-1">Configure and customize business metrics and parameters</p>
              
              {/* Configuration Import/Export Buttons */}
              {role === 'superadmin' && <div className="flex items-center gap-2 mt-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadConfig}
                    className="hidden"
                    id="config-upload"
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 hover:bg-blue-200 flex items-center text-sm"
                    onClick={() => document.getElementById('config-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Config
                  </button>
                </label>
                
                <button
                  type="button"
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg border border-green-300 hover:bg-green-200 flex items-center text-sm"
                  onClick={handleDownloadConfig}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Config
                </button>
              </div>}
            </div>
            <div className="flex flex-wrap items-center gap-y-2">
            <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input class="global-input"
                    type="checkbox" 
                    checked={moduleActive} 
                    onChange={(e) => setModuleActive(moduleActive===true ? false : true)}
                    className="sr-only global-input"
                    id="toggle-summary"
                  />
                  <label 
                    htmlFor="toggle-summary"
                    className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${moduleActive===true ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span 
                      className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${moduleActive===true ? 'translate-x-7' : 'translate-x-0'}`}
                    />
                  </label>
                </div>
                <button 
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 mr-2 flex items-center"
                  onClick={handleUpdateConfig}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save as Draft
                </button>
                <ConfigurationDiffButton
                  title="Business Insight Changes"
                  originalConfig={() => configWithExecutionSettings(getLoadedConfigForDiff(), loadedModuleExecution)}
                  modifiedConfig={() => configWithExecutionSettings(getCurrentConfigForDiff(), moduleExecution)}
                />
                {showSampleTest && <button 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                onClick={() => setShowSampleTestModal(true)}
              >
                Manage Test Set
              </button>}
                <div className="relative inline-block mx-2">
                  <button
                    className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                    onClick={() => setShowConfigDropdown((prev) => !prev)} style={{width: "10rem", justifyContent: "space-between"}}
                    type="button"
                  >
                    {configType}
                    <span className="ml-2">
                      {showConfigDropdown ? (
                        // Up arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        // Down arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </span>
                  </button>
                  {showConfigDropdown && (
                    <div className="absolute z-10 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg">
                      {['Live Version', 'Saved Draft', 'Default'].map(option => (
                        <div
                          key={option}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm ${configType === option ? 'text-indigo-600' : ''}`}
                          onClick={() => {
                            setConfigType(option);
                            setShowConfigDropdown(false);
                          }}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* <ModernTimeDropdown width="w-full mx-2" placeholder="Select Configuration" value={configType} onChange={(e)=>setConfigType(e.target.value)} options={['Reset to Default', 'Reset to Saved Draft', 'New Configuration']}/> */}
                {/* <button 
                className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 mr-2" style={{height: "40px", borderRadius: "4px"}}
                onClick={resetToDefault}
              >
                Reset to Default
              </button> */}
            <button 
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {setShowPreview(true); handleGenerateSampleOutputs(1);}} disabled={descriptionError}
            >
              Preview
            </button>
            </div>
          </div>
          

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 shrink-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Also run analytics for
              <span className="relative group">
                <button type="button" aria-label="About call-type settings" className="flex text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded">
                  <Info size={16} />
                </button>
                <span role="tooltip" className="absolute left-0 top-full z-50 mt-2 hidden w-64 rounded bg-gray-800 px-3 py-2 text-xs font-normal text-white shadow-lg group-hover:block group-focus-within:block">
                  Proper conversations run by default when the module is active. For other conversation types, enable the module option and each required parameter separately.
                </span>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {callTypeOptions.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500"
                    checked={moduleExecution[key] === 1}
                    onChange={e => setModuleExecution(prev => ({...prev, [key]: e.target.checked ? 1 : 0}))} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
              </div> : <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b border-gray-200" style={{height: "100%", overflowY: 'auto'}}>
              <h2 className="text-lg font-medium text-gray-800 mb-3">Parameters</h2>
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex-1 min-w-[12rem]"><ConfigurationTableSearch className="" label="Search parameters by name" value={tableSearch.parameters} onChange={value => setTableSearch({ ...tableSearch, parameters: value })} /></div>
                    <div className="flex flex-wrap items-center gap-4 shrink-0">
                <label className="flex items-center whitespace-nowrap text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={showArchivedParameters}
                      onChange={(e) => setShowArchivedParameters(e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Show Archived Parameters
                </label>
                  <button 
                    className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    style={{height: "40px", borderRadius: "4px"}}
                    onClick={handleAddParameter}
                  >
                    Add Parameter
                  </button>
                </div>
                  </div>
                  <div className="overflow-x-auto">
                <table className="configuration-table divide-y divide-gray-200">
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "10%" }} />
                    <col />
                    <col style={{ width: "80px" }} />
                    <col style={{ width: "110px" }} />
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Definition</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tag</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Options</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {parameterTableRows(getFilteredParameters(), tableSearch.parameters).map((param) => (
                      <tr key={param.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{param.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{param.definition}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {param.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {param.tag ? param.tag : 'NA'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {param.type === "Categorical - Single Value" || param.type === "Categorical - Multiple Values" ? (
                            <ConfigurationOptions options={param.options} />
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            
                            
                            {/* Toggle Switch for Active/Inactive (disabled for archived) */}
                            {param.status !== 'archived' && (
                              <>
                                <input 
                                  type="checkbox" 
                                  checked={!param.status || param.status === 'active'} 
                                  onChange={(e) => setBusinessInsightModule(businessInsightsModule.map(p => 
                                    p.id === param.id ? {
                                      ...p, 
                                      status: p.status === 'active' ? 'inactive' : 'active'
                                    } : p
                                  ))}
                                  className="sr-only global-input"
                                  id={`toggle-status-${param.id}`}
                                />
                                <label 
                                  htmlFor={`toggle-status-${param.id}`}
                                  className={`block h-5 w-12 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                                    !param.status || param.status === 'active' ? 'bg-indigo-600' : 'bg-gray-200'
                                  }`}
                                >
                                  <span 
                                    className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${
                                      !param.status || param.status === 'active' ? 'translate-x-8' : 'translate-x-0'
                                    }`}
                                  />
                                </label>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {param.status === 'archived' ? (
                            // Show restore button for archived parameters
                            <CustomTooltip text="Restore parameter" id={`restore-${param.id}`} position="left">
                              <button
                                onClick={() => handleRestoreParameter(param)}
                                className="hover:text-green-600 mr-4"
                              >
                                <TbRestore className='h-4 w-4'/>
                              </button>
                            </CustomTooltip>
                          ) : (
                            // Show edit and delete buttons for active/inactive parameters
                            <div className='flex items-center justify-end'>
                              <CustomTooltip text="Edit parameter" id={`edit-${param.id}`} position="left">
                                <button
                                  onClick={() => handleEditParameter(param)}
                                  className="text-indigo-600 hover:text-indigo-900 mr-4"
                                >
                                  <FaRegEdit/>
                                </button>
                              </CustomTooltip>
                              <CustomTooltip text="Archive parameter" id={`archive-${param.id}`} position="left">
                                <button
                                  onClick={() => {setShowArchiveModal(true); setArchiveParameter(param);}}
                                  className="text-red-600 hover:text-red-900 mr-4"
                                >
                                  <Archive className='h-4 w-4'/>
                                </button>
                              </CustomTooltip>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>}
          
          {/* Parameter Edit Form */}
          {editingParameter && (
            <div ref={parameterRef} className="bg-white rounded-lg shadow mb-6 p-6 border border-gray-200" style={{width: "85%", display: "block", margin: "auto", overflowY: "auto"}}>
              <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                {editingParameter.id ? "Edit Parameter" : "Add Parameter"}
                {(editingParameter && editingParameter.id) ? <div className="ml-1 relative">
                  <div className="group">
                    <Info className="h-4 w-4 text-gray-500 cursor-help ml-2" />
                    <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                      Name & Output type can't be modified.
                    </div>
                  </div>
                </div> : (<></>)}
              </h2>
              
              <div className="grid grid-cols-1 gap-6">
                {!showLoadConfigurePart && <>
                <div>
                  <label htmlFor="parameter-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Parameter Name
                  </label>
                  <input
                    type="text"
                    id="parameter-name"
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    value={editingParameter.name}
                    onChange={(e) => handleChangeParams(e)} disabled={editingParameter.id && editingParameter.id !== 0}
                  />
                  <div style={{display: "flex", justifyContent: "left", color: "red", fontSize: "12px"}}>{error}</div>
                </div>
                
                <ClassifierFields value={editingParameter} onChange={settings => setEditingParameter({ ...editingParameter, ...settings })} />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Context
                  </label>
                  <ModernTimeDropdown
                    width="w-full"
                    ariaLabel="Context"
                    value={editingParameter.context_scope === 'customer' ? 'Customer context' : 'Conversation context (default)'}
                    options={['Conversation context (default)', 'Customer context']}
                    onChange={(e) => setEditingParameter({...editingParameter, context_scope: e.target.value === 'Customer context' ? 'customer' : 'conversation'})}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {editingParameter.context_scope === 'customer'
                      ? 'Uses up to 50 recent conversations, including this call and non-connected calls.'
                      : 'Uses this conversation only.'}
                  </p>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Run this parameter for</p>
                    <div className="flex flex-wrap gap-4">
                      {callTypeOptions.map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500"
                            disabled={moduleExecution[key] !== 1}
                            checked={moduleExecution[key] === 1 && editingParameter[key] === 1}
                            onChange={e => setEditingParameter(prev => ({...prev, [key]: e.target.checked ? 1 : 0}))} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Enable the module call type, then opt this parameter in. Disabled parameters return None; existing calculation rules still apply.</p>
                  </div>

                </div>
                <div>
                  <label htmlFor="parameter-definition" className="block text-sm font-medium text-gray-700 mb-1">
                    Definition
                  </label>
                  <textarea
                    id="parameter-definition"
                    rows="3"
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    value={editingParameter.definition}
                    onChange={(e) => setEditingParameter({...editingParameter, definition: e.target.value})}
                  ></textarea>
                </div>

                <div className='relative'>
                  <label htmlFor="parameter-tag" className="block text-sm font-medium text-gray-700 mb-1">
                    Parameter Tag
                  </label>
                  <div className="relative">
        <div 
          className="border border-gray-300 rounded-md p-2 flex justify-between items-center cursor-pointer bg-white"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={selectedTag ? "text-gray-900" : "text-gray-400"}>
            {selectedTag || "Select a tag"}
          </span>
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
          </svg>
        </div>
        
        {isOpen && (
          <div className="absolute mt-1 w-full rounded-md shadow-lg bg-white z-10 border border-gray-200">
            <ul className="max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
              {tags.map((tag) => (
                <li
                  key={tag}
                  className="text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
                  onClick={() => handleSelectTag(tag)}
                >
                  {tag}
                </li>
              ))}
              
              {!isAddingNew ? (
                <li 
                  className="text-blue-600 cursor-pointer select-none relative py-2 pl-3 pr-9 border-t border-gray-200 hover:bg-blue-50"
                  onClick={() => setIsAddingNew(true)}
                >
                  + Add new tag
                </li>
              ) : (
                <li className="border-t border-gray-200 p-2">
                  <div className="flex items-center">
                    <input
                      type="text"
                      className="flex-grow border border-gray-300 rounded-md p-1 text-sm focus:outline-none global-input"
                      placeholder="Enter new tag"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNewTag();
                        }
                      }}
                      autoFocus
                    />
                    <button
                      className="ml-2 bg-blue-600 text-white px-2 py-1 rounded-md text-sm"
                      onClick={handleAddNewTag}
                    >
                      Add
                    </button>
                    <button
                      className="ml-1 text-gray-500 px-2 py-1 rounded-md text-sm"
                      onClick={() => setIsAddingNew(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
                </div>
                <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Parameter Type
          </label>
          <ModernTimeDropdown width='w-full' placeholder="Select Type" value={editingParameter.type === 'Date and Time' ? 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)' : editingParameter.type} options={classifierOutputOptions(editingParameter.subparameter_type, ['Yes/No/NA', 'Categorical - Single Value', 'Categorical - Multiple Values', 'Number', 'Text', 'Date and Time (ISO Format - YYYY-MM-DDTHH:MM:SS)'])}
            onChange={(e) => handleChangeParameterType(e.target.value)} isDisabled={editingParameter.id && editingParameter.id !== 0}/>
        </div>
                
                {(editingParameter.type === "Categorical - Single Value" || editingParameter.type === "Categorical - Multiple Values") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Options
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 text-gray-700 border rounded-l-lg focus:outline-none global-input"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        placeholder="Enter option"
                      />
                      <button
                        className="px-4 py-2 bg-indigo-600 text-white rounded-r-lg hover:bg-indigo-700 focus:outline-none global-input"
                        onClick={handleAddOption}
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editingParameter.options.map((tag, index) => (
                        <div key={index} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                          <span className="text-sm text-gray-700">{tag}</span>
                          <button
                            onClick={() => handleDeleteOption(tag)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <FaTimes style={{color: "slategray"}}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>}
                {
                  showLoadConfigurePart && (
                    <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                      <div className="loader"></div>
                      <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                    </div>
                  )
                }
                
                <div className="flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSaveParameter}
                    disabled={showLoadConfigurePart}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
        {
          showSampleTestModal && (
            <div>
              <div style={{position: "fixed", left: "2%", top: "6.5%", width: "96%", height: "90%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                zIndex: "999999999", padding: "0 15px"}}>
                <SampleTestModal clientID={client_id} serviceID={service_id} emailID={email} moduleName='business_insights' onClose={handleCloseSampleTestModal}/>
              </div>
            </div>
          )
        }
        {
          showArchiveModal && (
            <div style={{zIndex: "9999999999", position: "fixed", top: "0", left: "0", height: "100%", width: "100%", background: "rgba(0,0,0, 0.5)"}}>
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
                <div className="flex justify-between items-center p-4 pb-2">
                  <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Are you sure?</h2>
                  <button 
                    onClick={() => {
                      {setShowArchiveModal(false); setArchiveParameter(null);}
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                  <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>
                
                <div className="p-6 pt-2">
                  <p className="text-gray-500 text-sm text-left">This parameter will be archived and you won't see visualization for this parameter in the analytics tab.</p>
                </div>
                
                <div className="flex justify-end gap-3 p-6 pt-6">
                  <button
                    onClick={() => {setShowArchiveModal(false); setArchiveParameter(null);}}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
                  >
                    No
                  </button>
                  <button onClick={() => {handleArchiveParameter(archiveParameter); setShowArchiveModal(false);}}
                    className="px-6 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
        </div>
          )
        }
      </div>
    );
  }
  
  // Preview Screen
  return (
    <div className="p-3">
      <div className="mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Business Insights</h1>
            <p className="text-gray-500 text-sm mt-1">Sample results</p>
          </div>
          <div className="flex space-x-3">
            <button 
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none"style={{height: "40px", borderRadius: "4px"}}
              onClick={() => {setShowPreview(false); setPreviewData([]);}}
            >
              Go Back
            </button>
            <button onClick={() => {handleGenerateSampleOutputs(0); setPreviewData([]);}} className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none"style={{height: "40px", borderRadius: "4px"}}>
              Run Different Sample
            </button>
            <ConfigurationDiffButton
              title="Business Insight Changes"
              originalConfig={() => configWithExecutionSettings(getLoadedConfigForDiff(), loadedModuleExecution)}
              modifiedConfig={() => configWithExecutionSettings(getCurrentConfigForDiff(), moduleExecution)}
              triggerLabel="Deploy"
              showTriggerIcon={false}
              disabled={loading}
              triggerClassName={`px-4 py-1 h-10 ${loading ? 'bg-gray-500 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'}  text-white rounded  focus:outline-none`}
              onNext={() => setShowDeployConfirmation(true)}
            />
          </div>
        </div>
        <DeployConfirmationModal
          isOpen={showDeployConfirmation}
          confirmationText={`Changes will be deployed in ${client_id || 'N/A'}, ${service_id || 'N/A'} and ${module_name || 'business_insights'}`}
          onClose={() => setShowDeployConfirmation(false)}
          onConfirm={(versionDescription) => {
            setShowDeployConfirmation(false);
            deployModule(versionDescription);
          }}
        />

        <div style={{overflow: "auto", height: "75vh"}}>
        {loading ? (<div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
              </div>) : previewData.map((call, index) => (
          <div key={call.call_id} className="bg-white rounded-lg shadow my-2 overflow-hidden">
            <div className='p-4 border-b border-gray-200 bg-gray-50 cursor-pointer' onClick={() => toggleSample(index)}>
            <div 
                className=" flex justify-between items-center"
              >
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">{call.name}</span>
                </div>
                <svg 
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSamples[index] ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              <div className="text-sm text-gray-600 mt-1 capitalize" style={{fontSize: "12px"}}>
                        ID: {call.call_id} | Time: {call.call_time} | Employee: {call.employee_name} | Record Type: {call.record_type}
                      </div>
            </div>

            {expandedSamples[index] && (
            <div className="p-4 border shadow round-lg flex">
              <div className="border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.65"}}>
                <div 
                  className="p-2 bg-gray-50 border-b border-gray-200 cursor-pointer flex items-center" style={{justifyContent: "space-between"}}
                >
                  
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                    </svg>
                    <h3 className="font-medium text-gray-800">Output</h3>
                  </div>

                </div>
                <div className="grid gap-3 p-4" style={{height: "45vh", overflow: 'auto'}}>
                {call.processed_analytics && <FeedbackSection key="parameters" showMoreMap={showMoreMap} toggleShowMore={toggleShowMore} source_click={(source)=>handleSourceClick(source, call.segments)}
                  dataKey="Business Insights" data={call.processed_analytics.parameters} maxHeight="45vh" />}
              </div>
                </div>
              {/* Audio Section */}
              <div className="ml-2 border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.35"}}>
                            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center">
                              <div className="flex items-center">
                              {call.record_type === 'call' ? (
                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                </svg>
                  ) : (<IoChatbubbleOutline style={{marginRight: "0.5rem", height: "30px"}}/>)}
                                <h3 className="font-medium text-gray-800">{call.record_type === 'call' ? 'Audio' : 'Chat' }</h3>
                              </div>
                            </div>
                            {call.record_link && <div className="p-2 bg-white">
                              <audio controls key={call.record_link} ref={audioRef} onTimeUpdate={()=>handleAudioTimeUpdate(call.segments)} style={{ width: "96%", margin: "0 1rem", height: "40px" }}>
                                <source src={call.record_link} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>}
                            <hr/>
                            <div style={{fontSize: "13px", margin: "10px", overflow: 'auto', minHeight: "30vh", maxHeight: "40vh"}}>
                            {
                              call.record_type === 'call' && Array.isArray(call.segments) && call.segments.map((item, idx)=> {
                                        const isSelected = selectedSegmentIdx === idx;
                                        return (
                                          <div key={`segment_${idx}`} ref={(el) => (segmentRefs.current[idx] = el)} onClick={()=>toggleSegment(item, idx, call.record_link)} style={{marginBottom: "10px", borderLeft: isSelected ? "4px solid #6f6ffe":"", background: isSelected ? "rgba(249, 250, 251, 1)":"",
                                            padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                              <div style={{fontSize: "14px", color: "grey"}}>{item.speaker}</div>
                                              <div style={{fontSize: "14px", color: "grey"}}>{formatTime(item.start_time)}</div>
                                            </div>
                                            <div style={{fontSize: "15px"}}>{item.text}</div>
                                          </div>
                                        )
                                      })
                                    }
                            {
                              call.record_type === 'chat' && Array.isArray(call.segments) && call.segments.map((item, idx)=> {
                                return (
                                  <div key={`segment_${idx}`}
                                  data-message-id={item.message_id}
                                  style={{marginBottom: "10px", borderLeft: getBorderColor(item.actor_type), background: getBackgroundColor(item.actor_type),
                                    padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                      <div style={{fontSize: "10px", display: "flex", alignItems: "center"}}>
                                        <span className='' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.actor_type}</span>
                                        {item.message_type !== 'normal' && <span className=' ml-1' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.message_type}</span>}
                                      </div>
                                      <div style={{fontSize: "13px", color: "grey"}}>{formatEpochTime(item.timestamp)}</div>
                                    </div>
                                    <div style={{fontSize: "14px"}}><span style={{whiteSpace: "pre-line"}}>{item.content?.text?.content} <br/></span></div>
                                    {item.content.image && item.content.image.url && 
                                      <div style={{fontSize: "15px", whiteSpace: "pre-line"}}>
                                        <br/>
                                       Image Shared - {item.content.image.url}
                                      </div>
                                      }
                                    {
                                      item.content.buttons && item.content.buttons.length > 0 && (
                                        <div>
                                          <br/>
                                          {item.actor_type === 'user' ? (
                                            <span>Button - Selected Option - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          ) : (
                                            <span>Button - Options - {item.content.buttons.map(button => button.text).join(", ")}</span>
                                          )}
                                        </div>
                                      )
                                    }
                                  </div>
                                )
                                      })
                                    }
                            </div>
                          </div>
          
            </div>
            )}
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default BusinessInsightConfigure;
